import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn, LogOut, ArrowLeft, Truck, ChevronDown, Wrench, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { inputCls, labelCls, primaryBtn } from "@/components/portal/PortalAuth";

const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "—");

const FleetLogin = ({ wrongRole }) => {
  const { login, logout } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      if (u.role !== "fleet") {
        await logout();
        setError("This login is for fleet customers. Please use the account details given to you by the workshop.");
      }
    } catch (err) {
      setError(formatApiError(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md border border-white/10 bg-[#12151C] p-8 sm:p-10" data-testid="fleet-login-card">
      <div className="font-display text-3xl tracking-[0.12em] text-slate-50">SAGITTARIUS</div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Customer login — fleet & business</div>
      <p className="mt-6 text-sm leading-relaxed text-slate-400">Sign in with the account details provided by the workshop to see your fleet's service history.</p>
      {wrongRole && <div className="mt-4 border border-amber-600/40 bg-amber-600/10 px-4 py-3 text-xs text-amber-400" data-testid="fleet-wrong-role">You're signed in with a non-fleet account. Sign out and use your fleet login.</div>}
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="fleet-login-form">
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="fleet-email-input" />
        </div>
        <div>
          <label className={labelCls}>Password</label>
          <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="fleet-password-input" />
        </div>
        {error && <div className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="fleet-login-error">{error}</div>}
        <button type="submit" disabled={loading} className={primaryBtn} data-testid="fleet-login-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />} Sign in
        </button>
      </form>
    </motion.div>
  );
};

const VehicleRow = ({ v, index }) => {
  const [open, setOpen] = useState(false);
  const shown = open ? v.records : v.records.slice(0, 3);
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="border border-white/10 bg-[#12151C]" data-testid={`fleet-vehicle-${v.rego}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center justify-between gap-4 px-6 py-5 text-left" data-testid={`fleet-vehicle-toggle-${v.rego}`}>
        <div className="flex items-center gap-4">
          <span className="border border-teal-500/40 bg-teal-500/10 px-3 py-1.5 font-mono text-sm tracking-[0.2em] text-teal-400">{v.rego}</span>
          <div>
            <div className="text-base font-semibold text-slate-100">{[v.year, v.make, v.model].filter(Boolean).join(" ")}</div>
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
              {v.records.length} record{v.records.length === 1 ? "" : "s"} · Last service <span className="text-amber-500" data-testid={`fleet-last-service-${v.rego}`}>{fmtDate(v.last_service_date)}</span>
            </div>
          </div>
        </div>
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
          {open ? "Hide history" : "Full history"} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      <div className="border-t border-white/5 px-6 pb-5">
        {v.records.length === 0 && <p className="pt-4 font-mono text-[11px] text-slate-600" data-testid={`fleet-no-records-${v.rego}`}>No service records yet.</p>}
        {shown.map((r) => (
          <div key={r.id} className="flex flex-wrap items-start gap-x-6 gap-y-1 border-b border-white/5 py-3 last:border-0" data-testid={`fleet-record-${r.id}`}>
            <span className="w-28 shrink-0 font-mono text-xs text-slate-400">{fmtDate(r.service_date)}</span>
            <span className="flex-1 text-sm text-slate-200">{r.notes}</span>
            {r.odometer_km != null && <span className="font-mono text-[11px] text-slate-500">{r.odometer_km.toLocaleString()} km</span>}
          </div>
        ))}
        {!open && v.records.length > 3 && <p className="pt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">+ {v.records.length - 3} more — click for full history</p>}
      </div>
    </motion.div>
  );
};

const FleetDashboard = ({ user }) => {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/fleet/vehicles").then(({ data }) => setData(data)).catch(() => setData({ vehicles: [] }));
  }, []);
  return (
    <div className="App min-h-screen" data-testid="fleet-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0C10]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8">
          <Link to="/" className="flex items-center gap-3"><span className="font-display text-xl tracking-[0.15em] text-slate-50">SAGITTARIUS</span><span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Fleet Portal</span></Link>
          <button onClick={logout} data-testid="fleet-logout-btn" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:border-red-500/60 hover:text-red-400"><LogOut size={13} /> Sign out</button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-amber-500"><Building2 size={14} /> {user.business_name}</p>
        <h1 className="mt-3 font-display text-4xl uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl" data-testid="fleet-heading">Your fleet</h1>
        <p className="mt-4 text-sm text-slate-400">Signed in as {user.name} · {user.email}</p>
        {!data && <Loader2 size={18} className="mt-16 animate-spin text-amber-500" />}
        {data && data.vehicles.length === 0 && (
          <div className="mt-12 border border-white/10 bg-[#12151C] p-10 text-center" data-testid="fleet-empty">
            <Truck size={28} className="mx-auto text-slate-500" />
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-slate-400">No vehicles assigned yet</p>
            <p className="mt-3 text-sm text-slate-500">The workshop will add your vehicles here as they come in.</p>
          </div>
        )}
        {data && data.vehicles.length > 0 && (
          <div className="mt-10 space-y-4" data-testid="fleet-vehicles">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"><Wrench size={12} /> {data.vehicles.length} vehicle{data.vehicles.length === 1 ? "" : "s"}</p>
            {data.vehicles.map((v, i) => <VehicleRow key={v.id} v={v} index={i} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default function FleetPortal() {
  const { user } = useAuth();
  if (user === null) return <div className="App flex min-h-screen items-center justify-center"><Loader2 size={20} className="animate-spin text-amber-500" /></div>;
  if (!user || user.role !== "fleet")
    return (
      <div className="App flex min-h-screen flex-col items-center justify-center gap-8 px-5 py-16" data-testid="fleet-login-page">
        <FleetLogin wrongRole={!!user} />
        <Link to="/" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-amber-500" data-testid="fleet-back-home"><ArrowLeft size={12} /> Back to site</Link>
      </div>
    );
  return <FleetDashboard user={user} />;
}
